import { z } from 'zod';

/**
 * The facets of a filtered table: which values each multi-select filter can
 * offer, and how many rows carry each one.
 *
 * WHY THEY EXIST (founder, 2026-09-14): a filter option that no row of the
 * table carries is not offered. A status nobody is in, a bakery holding none of
 * the orders, is a choice that can only ever empty the table. A screen that
 * holds every row counts them itself; a screen whose list is paged on the
 * server cannot, because the page it holds is not the table - so the endpoint
 * answers the counts beside the page, and both sides count with the ONE
 * function below.
 *
 * THE RULE, once, so no two tables read it two ways: each filter is counted
 * over the rows every OTHER filter passes, with its own choice ignored. Counted
 * after its own choice, ticking one status would make every sibling status
 * vanish from the panel the reader is still ticking in; counted before every
 * choice, a bakery would stay on offer after the type filter removed all of its
 * orders. An empty choice narrows nothing. A value no row carries is absent,
 * never a zero.
 */

export const FilterFacet = z.object({
  value: z.string().max(200),
  count: z.int().positive(),
});
export type FilterFacet = z.infer<typeof FilterFacet>;

/** One multi-select filter over a row type: what is chosen, and what a row carries. */
export interface FacetDimension<Row> {
  /** The values the reader ticked; empty means every row. */
  readonly chosen: readonly string[];
  /** The value a row carries for this filter, or null when it carries none. */
  readonly valueOf: (row: Row) => string | null | undefined;
}

export interface FacetedRows<Row, Key extends string> {
  /** The rows every filter passes. */
  readonly rows: Row[];
  /** Per filter, the values it can offer - counted with the other filters applied. */
  readonly facets: Record<Key, FilterFacet[]>;
}

/**
 * `weightOf` is how many rows one row stands for - 1 unless given. A caller
 * whose database has already grouped identical rows (the Orders list's settled
 * history: every member of a group carries every value its head carries) hands
 * the group in once with its size, and every count is the same as for the rows
 * one by one.
 */
export function facetedFilter<Row, Key extends string>(
  rows: readonly Row[],
  dimensions: Readonly<Record<Key, FacetDimension<Row>>>,
  weightOf: (row: Row) => number = () => 1,
): FacetedRows<Row, Key> {
  const keys = Object.keys(dimensions) as Key[];
  const passes = (row: Row, ignore: Key | null): boolean =>
    keys.every((key) => {
      if (key === ignore) return true;
      const dimension = dimensions[key];
      if (dimension.chosen.length === 0) return true;
      const value = dimension.valueOf(row);
      return value !== null && value !== undefined && dimension.chosen.includes(value);
    });
  const facets = {} as Record<Key, FilterFacet[]>;
  for (const key of keys) {
    const counts = new Map<string, number>();
    const dimension = dimensions[key];
    for (const row of rows) {
      if (!passes(row, key)) continue;
      const value = dimension.valueOf(row);
      if (value !== null && value !== undefined) {
        counts.set(value, (counts.get(value) ?? 0) + weightOf(row));
      }
    }
    facets[key] = [...counts].map(([value, count]) => ({ value, count }));
  }
  return { rows: rows.filter((row) => passes(row, null)), facets };
}

/** How many rows a facet list says carry `value` - zero when it does not name it. */
export function facetCount(facets: readonly FilterFacet[] | undefined, value: string): number {
  return facets?.find((facet) => facet.value === value)?.count ?? 0;
}
